import { asHexCss, asHSLCss } from "./helpers/color-util.js";
import { toKebabCase } from "./helpers/string-util.js";
import jQuery from "jquery";

/**
 * `theme` handles theme variations for customers:
 * - Colors
 * - Branding Name
 * - Logo in agency
 *
 * Colors:
 *
 * Each theme has one main color: `brand-primary`. `brand-primary`
 * is the bright color used in the header and highlighted menu
 * items.
 *
 * Usage example:
 *
 *    theme.setup({
 *        colors: {
 *            'brand-primary': '#f79d2c',
 *            'brand-primary-dark': '#e78509',
 *            'brand-primary-verydark': '#cf7708'
 *        },
 *        brandingName: 'Wowow',
 *        logo: 'path/to/logo.png'
 *    });
 */
function theme(_, my = {}) {
	const that = {};

	/* Default colors that match the pwc theme */
	my.colors = {
		"brand-primary": "#00A0DE",
		"brand-primary-dark": "#008CC2",
		"brand-primary-verydark": "#007FB0",
		"brand-navbar-font-color": "#ffffff",
	};

	that.logo = "";
	that.brandingName = "";
	that.showSignOff = true;

	/**
	 * Public API method. Setup the theme object and update the
	 * injected styles accordingly.
	 */

	that.setup = function (spec) {
		my.colors = spec.colors;
		that.brandingName = spec.brandingName;

		that.backToAgencyIcon = spec.backToAgencyIcon;
		that.backToAgencyLabel = spec.backToAgencyLabel;
		that.agencyLogo = spec.agencyLogo;
		that.agencyPrintLogo = spec.agencyPrintLogo;
		that.agencyBrandingLogo = spec.agencyBrandingLogo;
		that.showSignOff = spec.showSignOff;
		that.advancedThemeColors = spec.advancedThemeColors ?? {};

		my.injectAdvancedThemeColorsStyle();
		my.injectPrimaryColorsStyle();
		my.injectFontPreloader();
		my.injectLogo();
	};

	/**
	 * Return whether or not the theme has settings for back to
	 * agency
	 */
	that.hasBackToAgencySettings = function () {
		return that.backToAgencyLabel;
	};

	my.injectLogo = function () {
		my.injectStyle(
			`${
				"\n" +
				".agency-leftmenu li.lm-li-toggler {\n" +
				'background-image: url(" '
			}${that.agencyBrandingLogo}");\n}`,
		);
	};

	// This is a fix for printing support with webfonts in Chrome.
	// See print.less for webfont preload and explanations.
	my.injectFontPreloader = function () {
		my.injectHTML(
			'<div class="font-preload"><span></span><span></span><span></span></div>',
		);
	};

	/**
	 * Inject colors for advanced theming
	 */
	my.injectAdvancedThemeColorsStyle = function () {
		let style = "";

		const { colors, pages } = that.advancedThemeColors;

		if (!pages || pages.length === 0) {
			return;
		}

		const scope = pages.map((page) => `body.${page}`).join(", ");

		colors.forEach((color) => {
			if (!color.value) return;

			if (
				color.key === "highlight" ||
				color.key === "keyFigureHighlight"
			) {
				const key = `${toKebabCase(color.key)}-color`;
				style += asHSLCss({ key, color: color.value });
				return;
			}

			const key = `kpi-${toKebabCase(color.key)}`;
			style += asHexCss({ key, color: color.value });
		});

		const result = `
			${scope} {
				${style}
            }
		`;

		my.injectStyle(result.trim());
	};

	my.injectPrimaryColorsStyle = function () {
		const style = `
			#leftmenu-toggler, #rightmenu-toggler {
				background-color: transparent !important;
			}
			#leftmenu-toggler:hover, #rightmenu-toggler:hover {
				background-color: transparent !important;
			}
			#main-navbar.navbar #brand-name h1 {
				color: ${my.colors["brand-navbar-font-color"]};
			}
			.header, #main-navbar.navbar, #admin-navbar, body.my-companies,
			.admin-body .lm-small .leftmenu li.lm-li-toggler,
			.companies-listing-table td a.block-link,.avatar.building,
			.leftmenu-container.agency-leftmenu.lm-small li.lm-li-toggler,
			.keyfigure-flag, .keyfigure-indicator {
				background-color: ${my.colors["brand-primary"]} !important
			}
			.keyfigure-flag-arrow {
				border-top-color: ${my.colors["brand-primary"]}!important;
			}
			#main-navbar.navbar .navbar-form .btn-group button,
			.admin-body .leftmenu li.lm-li-toggler,
			.btn.btn-navbar,
			body.my-companies .companies-listing-table td a.block-link:hover,
			.leftmenu li.active,
			.leftmenu li.active a:hover,
			.leftmenu li.active.current,
			.leftmenu li.active.current a:hover,
			.leftmenu-container ul.leftmenu li.lm-li-item:hover > a,
			.leftmenu ul.lm-sub-menu li a:hover,
			.lm-small .leftmenu li:hover li:hover a,
			.lm-small .leftmenu li.lm-li-parent:hover > a {
				background-color: ${my.colors["brand-primary-dark"]} !important;
				border-color: transparent !important;
			}
			.finsit-element_theme.period-picker {
				--select-button-background-color: ${my.colors["brand-primary-dark"]};
    			--select-background-color-hover: ${my.colors["brand-primary-dark"]};
			}
			.leftmenu-container.agency-leftmenu ul.leftmenu li.lm-li-toggler {
				background-color: ${my.colors["brand-primary-dark"]} !important;
				border-color: ${my.colors["brand-primary-dark"]} !important;
			}
			.client-quickbuttons-container a, .client-quickbuttons-container .header-icon,
			.fp-tool-buttons div.fp-back,
			.leftmenu li.current,
			.leftmenu li.current a:hover,
			.lm-small .leftmenu li:hover li.current a {
				background-color: ${my.colors["brand-primary-verydark"]} !important;
			}
			.admin-body .leftmenu-container ul.leftmenu li.lm-li-toggler,
			.lm-small .leftmenu li:hover,"
			.lm-small .leftmenu li.lm-li-parent:hover,"
			.leftmenu-container ul.leftmenu li.lm-li-item.current:hover,
			.leftmenu-container.agency-leftmenu.lm-small li.lm-li-toggler,
			.leftmenu li.lm-li-item.current {
				border-color: ${my.colors["brand-primary-verydark"]}!important;
			}
			/* Active left menu color FTGP-2202 */
			.leftmenu-container ul.leftmenu li.lm-li-item ul.lm-sub-menu li.lm-li-sub-item.active a,
			.leftmenu-container ul.leftmenu li.lm-li-item ul.lm-sub-menu li.lm-li-sub-item.current a {
				background-color: ${my.colors["brand-primary-verydark"]}!important;
			}
			.leftmenu-container .leftmenu-consultant .back-to-agency-icon {
				background-color: ${my.colors["brand-primary-dark"]} !important;
			}
			#application-loading img {
				background-color: ${my.colors["brand-primary-dark"]} !important;
			}
			.leftmenu-container ul.leftmenu li.lm-li-item:hover {
				border-color: ${my.colors["brand-primary-dark"]} !important;
			}
			.company-type-cards .card-container .card-header .default {
				background-color: ${my.colors["brand-primary"]} !important;
			}
			.agency-viewport .checklist-template-editor .accordion .panel .panel-heading {
				background-color: ${my.colors["brand-primary-verydark"]};
			}
			.agency-viewport .checklist-template-editor .accordion .panel.collapsed .panel-heading:hover {
				background-color: ${my.colors["brand-primary-verydark"]};
			}
			.agency-viewport .checklist-template-editor .accordion .panel .panel-body {
				border-color: ${my.colors["brand-primary-verydark"]};
			}`;

		my.injectStyle(style);
		my.setPageTitle();
	};

	my.injectStyle = function (styleString) {
		const style = `<style>${styleString}</style>`;
		jQuery("body").append(style);
	};

	my.injectHTML = function (htmlString) {
		jQuery("body").append(htmlString);
	};

	my.setPageTitle = function () {
		if (!that.brandingName) {
			return;
		}
		document.title = that.brandingName;
	};

	return that;
}

export { theme as themeBuilder };

export default theme();
